import { useEffect, useRef, useState } from "react";

/**
 * Prophets custom cursor: silver dot + ring, subtle magnetic ring enlargement
 * over interactive elements. Mounts only on fine-pointer hover-capable
 * devices with motion enabled. Zero external dependencies.
 */
export function CustomCursor({ rootRef }) {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const rafRef = useRef(0);
  const target = useRef({ x: -100, y: -100 });
  const current = useRef({ x: -100, y: -100 });
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const fine = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!fine || reduced) return undefined;
    setEnabled(true);
    rootRef.current?.classList.add("cursor-active");
    return () => rootRef.current?.classList.remove("cursor-active");
  }, [rootRef]);

  useEffect(() => {
    if (!enabled) return undefined;
    const onMove = (e) => {
      target.current.x = e.clientX;
      target.current.y = e.clientY;
    };
    const enterHover = () => ringRef.current?.classList.add("is-hover");
    const leaveHover = () => ringRef.current?.classList.remove("is-hover");

    const hoverables = () =>
      rootRef.current?.querySelectorAll(
        'a, button, [role="button"], [data-cursor="hover"]',
      ) || [];
    const attach = () => {
      hoverables().forEach((el) => {
        el.addEventListener("mouseenter", enterHover);
        el.addEventListener("mouseleave", leaveHover);
      });
    };
    const detach = () => {
      hoverables().forEach((el) => {
        el.removeEventListener("mouseenter", enterHover);
        el.removeEventListener("mouseleave", leaveHover);
      });
    };
    attach();

    const tick = () => {
      // Ring lerps toward target for smooth trail, dot follows tightly.
      current.current.x += (target.current.x - current.current.x) * 0.22;
      current.current.y += (target.current.y - current.current.y) * 0.22;
      if (ringRef.current) {
        ringRef.current.style.transform = `translate3d(${current.current.x - 16}px, ${current.current.y - 16}px, 0)`;
      }
      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${target.current.x - 2}px, ${target.current.y - 2}px, 0)`;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    window.addEventListener("mousemove", onMove, { passive: true });
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("mousemove", onMove);
      detach();
    };
  }, [enabled, rootRef]);

  if (!enabled) return null;
  return (
    <>
      <div ref={ringRef} className="prophets-cursor-ring" aria-hidden="true" />
      <div ref={dotRef} className="prophets-cursor-dot" aria-hidden="true" />
    </>
  );
}
